import {
  pxTovW,
  pxToRem,
  IconWrapper,
  IStyles,
  ImageWrapper,
  InputField,
  theme,
} from '@geneo2-web/shared-ui';
import {
  Box,
  Button,
  Divider,
  Popper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  ChapterIdsInfo,
  MultiSubjectSectionStatusEnum,
} from '@protos/learning_management/lms.db_pb';
import { ChangeEvent, MouseEvent, useState } from 'react';
import { type DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';

const styles: IStyles = {
  cardRoot: {
    borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
    boxShadow: '0px 0px 6px #00000029',
    padding: { xs: pxToRem(10), md: pxTovW(20) },
    width: { xs: '95%', md: pxTovW(1100), lg: pxTovW(879) },
    marginX: 'auto',
    backgroundColor: '#ffffff',
  },
  subjectTopicChapterBox: {
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(20) },
  },
  subjectMarksReorderDeleteBox: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  subjectMarksBox: {
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(22) },
    alignItems: 'center',
  },
  marksBox: {
    backgroundColor: '#DDFAEC',
    borderRadius: { xs: pxToRem(8), md: pxTovW(8) },
    paddingX: { xs: pxToRem(10), md: pxTovW(15) },
    paddingY: { xs: pxToRem(5), md: pxTovW(10) },
  },
  reorderDeleteBox: {
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(15) },
    alignItems: 'center',
  },
  sectionTitleBox: {
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(15) },
    marginTop: { xs: pxToRem(10), md: pxTovW(20) },
  },
  sectionTypo: {
    fontFamily: 'Poppins',
    fontSize: { xs: pxToRem(18), md: pxTovW(18) },
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    minWidth: '20%',
    maxWidth: '60%',
  },
  sectionTitleInput: {
    maxWidth: '60%',
    boxSizing: 'border-box',
    '& input': {
      maxWidth: '100%',
      width: '100%',
      height: { xs: pxToRem(25), md: pxTovW(30) },
      fontFamily: 'Poppins',
      fontSize: { xs: pxToRem(18), md: pxTovW(18) },
    },
    '& .MuiOutlinedInput-root': {
      padding: '0',
      borderBottom: `2px solid #007CDC`,
      backgroundColor: 'transparent',
      paddingX: { xs: pxToRem(10), md: pxTovW(10) },
      borderRadius: '0',
    },
  },
  editTopicIcon: {
    height: { xs: pxToRem(21), md: pxTovW(21) },
    width: { xs: pxToRem(21), md: pxTovW(21) },
    cursor: 'pointer',
    border: '1px solid #0AA34F',
    borderRadius: { xs: pxToRem(6), md: pxTovW(6) },
  },
  chaptersAndInfoBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(15) },
    marginTop: { xs: pxToRem(10), md: pxTovW(20) },
  },
  chaptersAddedBox: {
    backgroundColor: '#DDFAEC',
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    paddingX: { xs: pxToRem(10), md: pxTovW(15) },
    paddingY: { xs: pxToRem(5), md: pxTovW(5) },
    width: 'max-content',
  },
  queDiffCreateAssessBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  queDiffBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(42), lg: pxTovW(20) },
  },
  noOfQueBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(5), md: pxTovW(10) },
    alignItems: 'center',
  },
  diffLevelBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(5), md: pxTovW(10) },
    alignItems: 'center',
  },
  completedBox: {
    background: '#DAFFEB',
    borderRadius: { xs: pxToRem(6), md: pxTovW(10) },
    paddingX: { xs: pxToRem(10), md: pxTovW(18) },
    paddingY: { xs: pxToRem(5), md: pxTovW(10) },
    height: { xs: pxToRem(28), md: 'unset' },
    boxSizing: 'border-box',
  },
  libraryButton: {
    backgroundColor: '#FFF8DC',
    border: '1px solid #F8C807',
    borderRadius: { xs: pxToRem(6), md: pxTovW(10) },
    paddingX: { xs: pxToRem(10), md: pxTovW(10) },
    paddingY: { xs: pxToRem(5), md: pxTovW(10) },
    height: { xs: pxToRem(28), md: 'unset' },
    boxSizing: 'border-box',
  },
  createAssessButton: {
    backgroundColor: '#EBF6FF',
    border: '1px solid #007CDC',
    borderRadius: { xs: pxToRem(6), md: pxTovW(10) },
    paddingX: { xs: pxToRem(10), md: pxTovW(10) },
    paddingY: { xs: pxToRem(5), md: pxTovW(10) },
    height: { xs: pxToRem(28), md: 'unset' },
    boxSizing: 'border-box',
  },
};

interface IPaperDetailsCardProps {
  totalSections?: number;
  subjectIcon: string;
  subjectColor: string;
  subjectName: string;
  marks: number;
  reorderCardHandler: () => void;
  deleteCardHandler: () => void;
  sectionTitle: string;
  onChangeSectionTitle: (value: string) => void;
  onSectionTitleEditDone?: () => void | Promise<void>;
  // chapterList: string[];
  chapterList?: ChapterIdsInfo[];
  noOfQuestions?: number;
  difficultyLevel?: string;
  chooseLibraryHandler?: () => void;
  createAssessmentHandler: () => void;
  assessmentStatus: MultiSubjectSectionStatusEnum;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  published?: boolean;
}

export const PaperDetailsCard = (props: IPaperDetailsCardProps) => {
  const {
    totalSections,
    subjectIcon,
    subjectColor,
    subjectName,
    marks,
    reorderCardHandler,
    deleteCardHandler,
    sectionTitle,
    onChangeSectionTitle,
    onSectionTitleEditDone,
    chapterList,
    noOfQuestions,
    difficultyLevel,
    chooseLibraryHandler,
    createAssessmentHandler,
    assessmentStatus,
    dragHandleProps,
    published,
  } = props;

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [sectionTitleEdit, setSectionTitleEdit] = useState(false);

  const uniqueChapterList = () => {
    // get unique chapterList from chapterList
    return chapterList?.filter(
      (chapter, index, self) =>
        index === self.findIndex((t) => t.chapterId === chapter.chapterId)
    );
  };

  return (
    <Box sx={styles.cardRoot}>
      <Box sx={styles.subjectTopicChapterBox}>
        {/* //& Subject Image */}
        <Box>
          <Box
            sx={{
              width: { xs: pxToRem(70), md: pxTovW(126) },
              height: { xs: pxToRem(90), md: pxTovW(139) },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              // backgroundColor: '#FFF7D5',
              backgroundColor: subjectColor,
              borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
            }}
          >
            <ImageWrapper
              name={'subjects/english'}
              path={subjectIcon}
              type="png"
              parentFolder="icons"
              styles={{
                width: { xs: pxToRem(45), md: pxTovW(63) },
                height: { xs: pxToRem(45), md: pxTovW(63) },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }}>
          {/* //& Subject / marks / reorder / delete */}
          <Box sx={styles.subjectMarksReorderDeleteBox}>
            <Box sx={styles.subjectMarksBox}>
              <Typography variant="h3" fontWeight="bold" color="primary">
                {subjectName}
              </Typography>

              <Box sx={styles.marksBox}>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  sx={{ fontFamily: 'poppins' }}
                >
                  {marks} Marks
                </Typography>
              </Box>
            </Box>

            {published || totalSections === 1 ? null : (
              <Box sx={styles.reorderDeleteBox}>
                <Box
                  {...(dragHandleProps ?? {})}
                  sx={{
                    display: 'inline-flex',
                    cursor: dragHandleProps ? 'grab' : 'pointer',
                  }}
                  onClick={!dragHandleProps ? reorderCardHandler : undefined}
                >
                  <ImageWrapper
                    name="reorder"
                    parentFolder="icons"
                    type="png"
                    styles={{
                      width: { xs: pxToRem(10), md: pxTovW(15) },
                      height: { xs: pxToRem(15), md: pxTovW(20) },
                    }}
                  />
                </Box>

                <Button onClick={deleteCardHandler}>
                  <ImageWrapper
                    name="delete-small"
                    parentFolder="icons"
                    type="png"
                    styles={{
                      width: { xs: pxToRem(13), md: pxTovW(18) },
                      height: { xs: pxToRem(15), md: pxTovW(20) },
                      cursor: 'pointer',
                    }}
                  />
                </Button>
              </Box>
            )}
          </Box>

          {/* //& Section Title */}
          <Box sx={styles.sectionTitleBox}>
            {!sectionTitle && published ? null : !sectionTitleEdit ||
              published ? (
              <Typography
                variant="h4"
                color={sectionTitle ? 'initial' : '#AAAAAA'}
                sx={styles.sectionTypo}
              >
                {sectionTitle || 'Add Section Title'}
              </Typography>
            ) : (
              <InputField
                variant="outlined"
                fullWidth
                placeholder="Add Section Title"
                type="text"
                value={sectionTitle}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChangeSectionTitle(e.target.value)
                }
                onBlur={() => {
                  setSectionTitleEdit(false);
                  onSectionTitleEditDone?.();
                }}
                sx={styles.sectionTitleInput}
              />
            )}

            {published ? null : (
              <IconWrapper
                name={sectionTitleEdit ? 'correct-green-square' : 'edit'}
                parentFolder="icons"
                type="png"
                size="md"
                onClick={() => {
                  const wasEditing = sectionTitleEdit;
                  setSectionTitleEdit((prev) => !prev);
                  if (wasEditing) {
                    onSectionTitleEditDone?.();
                  }
                }}
                customSx={styles.editTopicIcon}
              />
            )}
          </Box>

          {/* //& Chapters Added */}
          <Box sx={styles.chaptersAndInfoBox}>
            <Box sx={styles.chaptersAddedBox}>
              <Typography
                variant="smallText"
                color="secondary.main"
                fontWeight="regular"
                sx={{ fontFamily: 'poppins' }}
              >
                {/* {chapterList.length < 2
                  ? `${chapterList.length} Chapter Added`
                  : `${chapterList.length} Chapters Added`} */}
                {/* {chapterList.length === 1
                  ? `${chapterList.length} Chapter Added`
                  : `${chapterList.length} Chapters Added`} */}
                {uniqueChapterList()?.length === 1
                  ? `${uniqueChapterList()?.length ?? 0} Chapter Added`
                  : `${uniqueChapterList()?.length ?? 0} Chapters Added`}
              </Typography>
            </Box>

            {(uniqueChapterList()?.length ?? 0) > 0 ? (
              <InfoNode
                info={uniqueChapterList()?.map((chapter) => chapter.name) ?? []}
              />
            ) : null}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ marginY: { xs: pxToRem(10), md: pxTovW(15) } }} />

      {/* //& No of Questions / Difficulty Level / Assess button */}
      <Box sx={styles.queDiffCreateAssessBox}>
        <Box sx={styles.queDiffBox}>
          <Box sx={styles.noOfQueBox}>
            <Typography
              variant="h5"
              color="gray"
              fontWeight="regular"
              sx={{
                fontFamily: 'poppins',
                fontSize: { xs: pxToRem(9), md: pxTovW(16) },
              }}
            >
              {isMobile ? 'No. of Questions' : 'Number of Questions'}
            </Typography>
            <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight="medium">
              {noOfQuestions ? noOfQuestions : '-'}
            </Typography>
          </Box>

          <Divider orientation="vertical" variant="middle" flexItem />

          <Box sx={styles.diffLevelBox}>
            <Typography
              variant="h5"
              color="gray"
              fontWeight="regular"
              sx={{
                fontFamily: 'poppins',
                fontSize: { xs: pxToRem(9), md: pxTovW(16) },
              }}
            >
              Overall Difficulty Level
            </Typography>
            <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight="medium">
              {difficultyLevel ? difficultyLevel : '-'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: { xs: pxToRem(10), md: pxTovW(15) } }}>
          {chooseLibraryHandler && (
            <Button
              disabled={
                assessmentStatus ===
                MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_APPROVED
              }
              sx={styles.libraryButton}
              onClick={chooseLibraryHandler}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                color={
                  assessmentStatus ===
                  MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_APPROVED
                    ? 'gray'
                    : 'black'
                }
                sx={{
                  fontFamily: 'poppins',
                  fontSize: { xs: pxToRem(10), md: 'unset' },
                }}
              >
                CHOOSE LIBRARY
              </Typography>
            </Button>
          )}

          {assessmentStatus ===
          MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_APPROVED ? (
            <Button sx={styles.completedBox} onClick={createAssessmentHandler}>
              <Typography
                variant="h5"
                color="secondary.main"
                fontWeight="bold"
                sx={{
                  fontFamily: 'poppins',
                  fontSize: { xs: pxToRem(10), md: 'unset' },
                }}
              >
                {/* COMPLETED */}
                MODIFY
              </Typography>
            </Button>
          ) : (
            <Button
              sx={styles.createAssessButton}
              onClick={createAssessmentHandler}
            >
              <Typography
                variant="h5"
                color="primary.main"
                fontWeight="bold"
                sx={{
                  fontFamily: 'poppins',
                  fontSize: { xs: pxToRem(10), md: 'unset' },
                }}
              >
                {assessmentStatus ===
                MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_DRAFT
                  ? isMobile
                    ? 'EDIT'
                    : 'EDIT ASSESSMENT'
                  : isMobile
                  ? 'CREATE'
                  : 'CREATE ASSESSMENT'}
              </Typography>
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// =====================================================

interface IInfoNodeProps {
  info: string[];
}
const InfoNode = (props: IInfoNodeProps) => {
  const { info } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  return (
    <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <IconWrapper
        name="tooltip-black"
        parentFolder="icons"
        type="png"
        size="small"
        customSx={{
          height: { xs: pxToRem(14), md: pxTovW(20), lg: pxTovW(14) },
          width: { xs: pxToRem(14), md: pxTovW(20), lg: pxTovW(14) },
          cursor: 'pointer',
        }}
      />

      <Popper id={id} open={open} anchorEl={anchorEl} placement="top">
        <Box
          sx={{
            backgroundColor: '#050505',
            color: '#FFFFFF',
            padding: { xs: pxToRem(10), md: pxTovW(10) },
            minWidth: { xs: pxToRem(240), md: pxTovW(240) },
            borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
            marginBottom: { xs: pxToRem(35), md: pxTovW(50) },
            marginLeft: { xs: pxToRem(20), md: pxTovW(0) },
            zIndex: 1000,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              // gap: { xs: pxToRem(16), md: pxTovW(16) },
              border: '1px solid #FFFFFF',
              borderRadius: { xs: pxToRem(16), md: pxTovW(16) },
            }}
          >
            <Typography
              textAlign="center"
              variant="h3"
              fontWeight="bold"
              color="white"
              sx={{ padding: { xs: pxToRem(10), md: pxTovW(10) } }}
            >
              Selected Chapters
            </Typography>

            {info.map((info, index) => (
              <Typography
                key={info}
                fontWeight="regular"
                sx={{
                  fontSize: { xs: pxToRem(14), md: pxTovW(16) },
                  fontWeight: 600,
                  borderTop: '1px solid #FFFFFF',
                  padding: { xs: pxToRem(10), md: pxTovW(10) },
                }}
              >
                {index + 1}. {info}
              </Typography>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            width: 0,
            height: 0,
            borderLeft: {
              xs: pxToRem(50) + ' solid transparent',
              md: pxTovW(50) + ' solid transparent',
            },
            borderRight: {
              xs: pxToRem(50) + ' solid transparent',
              md: pxTovW(50) + ' solid transparent',
            },
            borderTop: {
              xs: pxToRem(50 * Math.sqrt(3)) + ' solid #050505',
              md: pxTovW(50 * Math.sqrt(3)) + ' solid #050505',
            }, // x * √3, for equilateral proportions
            position: 'absolute',
            left: { xs: '35%', md: '35%' },
            bottom: { xs: pxToRem(10), md: pxTovW(20) },
            margin: 'auto',
          }}
        ></Box>
      </Popper>
    </Box>
  );
};
